import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'
import { OtpDigitInput } from '../atoms/OtpDigitInput'

/**
 * TotpVerifyStep — the screen the user sees when the pipeline runtime
 * pauses at `mfa.verify` with `factor: "totp"`. The user types the
 * 6-digit code from their authenticator app (Google Authenticator,
 * 1Password, Authy, ...) and submits.
 *
 * Unlike `VerificationOtpStep`, there is no QR, no masked recipient
 * heading, and no Resend control — the code is generated on-device by
 * the authenticator, not dispatched server-side, so there's nothing
 * the parent can re-send.
 *
 * Composition: heading copy + sub-heading + OtpDigitInput (atom) +
 * Button (atom). No network calls — `onSubmit` comes from the parent
 * so the same molecule works for login MFA verify, step-up auth,
 * sensitive-action re-auth, etc.
 *
 * A11y:
 *   - OTP cells live in a labelled group ("authenticator code") with
 *     per-cell labels ("Digit N of 6, authenticator code").
 *   - Inline error is `role="alert"` + `aria-live="polite"` so AT users
 *     hear the failure when the parent sets `error`.
 *   - When `error` is non-empty every cell is `aria-invalid="true"`.
 */

export interface TotpVerifyStepProps {
  /** Number of cells. Default 6 (TOTP RFC 6238 default). */
  codeLength?: number
  /** Called when the user submits the code (Verify button or Enter). */
  onSubmit: (code: string) => void | Promise<void>
  /** Inline error from the parent. */
  error?: string
  /** Set true while submit is in flight. Disables input + shows "Verifying…". */
  busy?: boolean
  /**
   * Override the visible heading. Default: "Enter the code from your
   * authenticator app". Use for step-up flows where the wording differs
   * ("Re-enter your authenticator code to confirm").
   */
  heading?: string
  /** Optional helper text rendered under the heading. */
  helperText?: string
  /**
   * Optional pause identity. When the parent passes a fresh pause id
   * (because the pipeline advanced from one verify step to another and
   * reused the component instance), the digit state resets so the user
   * doesn't accidentally submit the prior step's code. Same
   * single-strike pattern as VerificationOtpStep.
   */
  pauseId?: string
  /** Extra classes for the wrapper. */
  className?: string
}

export const TotpVerifyStep = React.forwardRef<HTMLDivElement, TotpVerifyStepProps>(
  (
    {
      codeLength = 6,
      onSubmit,
      error,
      busy = false,
      heading = 'Enter the code from your authenticator app',
      helperText = 'Open Google Authenticator, 1Password, or whichever app you use, and type the current 6-digit code.',
      pauseId,
      className,
    },
    ref,
  ) => {
    const [code, setCode] = React.useState('')

    // Reset code when the pause identity changes. Without this, a stale
    // code typed for a prior pause could auto-paste forward and burn the
    // freshly-minted attempt (single-strike-out semantics that bit us in
    // Phase-2 Row 4; see project_phase2_verification_check_aborts_on_first_invalid).
    // biome-ignore lint/correctness/useExhaustiveDependencies: setCode is a stable React state setter and pauseId IS the identity-change trigger we want
    React.useEffect(() => {
      setCode('')
    }, [pauseId])

    const handleSubmit = React.useCallback(async () => {
      if (busy) return
      if (code.length !== codeLength) return
      await onSubmit(code)
    }, [busy, code, codeLength, onSubmit])

    const submitDisabled = busy || code.length !== codeLength

    return (
      <div ref={ref} className={cn('flex flex-col gap-4', className)}>
        <div className="flex flex-col gap-1 text-center">
          <h2 className="text-heading-sm font-semibold text-ia-text">{heading}</h2>
          {helperText ? <p className="text-body-sm text-ia-text-secondary">{helperText}</p> : null}
        </div>

        <OtpDigitInput
          value={code}
          onChange={setCode}
          onEnter={handleSubmit}
          length={codeLength}
          autoFocus
          disabled={busy}
          invalid={!!error}
          aria-label="authenticator code"
        />

        {/* Reserved error row so layout doesn't jump in/out. The live
            region stays mounted (empty when no error) which keeps SR
            announcements deterministic. min-h-5 reserves the space —
            no filler text needed, otherwise the literal leaks into the
            a11y tree as an "alert: placeholder" announcement. */}
        <div
          role="alert"
          aria-live="polite"
          className={cn('min-h-5 text-center text-body-sm', error ? 'text-ia-error' : '')}
        >
          {error}
        </div>

        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={submitDisabled}
          loading={busy}
          className="w-full"
        >
          {busy ? 'Verifying…' : 'Verify'}
        </Button>
      </div>
    )
  },
)
TotpVerifyStep.displayName = 'TotpVerifyStep'
